import React, { useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { PrimaryButton } from '../../../components/Button';
import AuthLayout from '../AuthLayout';
import TosMessage from './forms/TosMessage';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Cognito from '../utils/Cognito';
import useBroadcastChannel from '../../../utils/hooks/useBroadcastChannel';
import useBroadcastIframe from '../../../utils/hooks/useBroadcastIframe';
import { POPUP_WINDOW_NAME } from '../utils/utils';
import { validateRedirectUrl } from '../../../utils/embedUrlValidation';

const LogInButton = styled(PrimaryButton)`
  &&& {
    width: 100%;
    height: 38px;
    font-size: 14px;
  }
  margin: 16px 0;
`;

const EmbedLogin = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const handleLoginMessage = useCallback(
    (message: MessageEvent<{ type: string; redirectUrl: string }>) => {
      // When we receive a login message, we're already authenticated
      const redirectUrl =
        message?.data?.redirectUrl || searchParams.get('redirect');
      const validatedUrl = validateRedirectUrl(redirectUrl);
      if (validatedUrl) {
        navigate(validatedUrl);
      } else {
        navigate('/');
      }
    },
    [navigate, searchParams],
  );

  // Listen for login messages via BroadcastChannel
  useBroadcastChannel({
    channelName: 'login',
    messageHandler: handleLoginMessage,
  });

  // Listen for login messages via iframe postMessage
  useBroadcastIframe({
    messageType: 'login',
    messageHandler: handleLoginMessage,
  });

  // If not in iframe and we're authenticated, redirect to the original URL
  useEffect(() => {
    if (window === window.parent) {
      // First check if we're authenticated
      Cognito.refreshToken().then(() => {
        const redirectUrl = searchParams.get('redirect');
        const validatedUrl = validateRedirectUrl(redirectUrl);
        if (validatedUrl) {
          navigate(validatedUrl);
        } else {
          navigate('/');
        }
      });
    }
  }, [navigate, searchParams]);

  const onClick = () => {
    const loginUrl = Cognito.getLoginPageUrl();

    // Calculate center position
    const width = 600;
    const height = 800;
    const left = (window.screen.width - width) / 2;
    const top = (window.screen.height - height) / 2;

    window.open(
      loginUrl,
      POPUP_WINDOW_NAME,
      `width=${width},height=${height},left=${left},top=${top}`,
    );
  };

  return (
    <AuthLayout heading="Welcome to Kubit">
      <LogInButton type="button" onClick={onClick}>
        Log In
      </LogInButton>
      <TosMessage />
    </AuthLayout>
  );
};

export default EmbedLogin;
